import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowDownToLine,
  ArrowUpFromLine,
  ChevronRight,
  FileDown,
  Filter,
  Printer,
  RotateCcw,
  ScrollText,
  Search,
  X,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useContexto } from '@/hooks/useContexto';
import { useAbrirNoEdge } from '@/hooks/useAbrirNoEdge';
import { useAtualizarParams } from '@/hooks/useUrlEstado';
import { baixarCsv, carimboArquivo } from '@/lib/csv';
import { cn, numero } from '@/lib/utils';
import { Botao } from '@/componentes/ui/botao';
import { Segmentado } from '@/componentes/ui/segmentado';
import { LogMovimentacoes } from '@/componentes/auditoria/LogMovimentacoes';
import { EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import { Campo, Esqueleto, Pilula } from '@/componentes/ui/primitivos';
import { CabecalhoColuna, useGrade, type ColunaGrade } from '@/componentes/tabela/GradeFiltravel';

/**
 * Kardex — a razão do estoque de um SKU num centro, para auditoria.
 *
 * Espelha o relatório `ESTOQUE_MOVIMENTACAO` do Procfit ("Movimentação de
 * Produtos no Período"), com o que o WMS tem a mais: o LOCALIZADOR, a TELA
 * QUE ORIGINOU o lançamento e o saldo acumulado linha a linha.
 *
 * A tela de origem é o que separa "saiu 1 un" de "saiu 1 un porque alguém
 * digitou uma nota de faturamento" — em auditoria, a segunda frase é a que
 * responde a pergunta.
 *
 * O PDF sai pela impressão do navegador, não por biblioteca embarcada. Não é
 * economia de dependência: relatório de auditoria precisa de paginação com
 * cabeçalho repetido, texto selecionável e o carimbo de emissão, e é
 * exatamente isso que o motor de impressão entrega — com o bônus de o operador
 * escolher papel, margem e escala. As regras de página vivem em
 * `@media print`, e a tela inteira fora da folha some.
 */

interface LinhaKardex {
  lancamento: number;
  data: string;
  dataHora: string;
  tipoMovimento: number | null;
  tipoDescricao: string;
  documento: number | null;
  /** O registro da tela de origem — a outra metade do rastro. */
  regMasterOrigem: number | null;
  localizador: number | null;
  endereco: string | null;
  /** Empresa da POSIÇÃO — nem sempre é a do centro. */
  filial: number | null;
  formulario: number | null;
  formularioDescricao: string | null;
  formularioModulo: string | null;
  entrada: number;
  saida: number;
  saldoDepois: number;
  /** Perna de transferência entre centros: o número e as duas pontas. */
  transferencia?: {
    transferencia: number;
    centroOrigem: number;
    centroOrigemNome: string | null;
    centroDestino: number;
    centroDestinoNome: string | null;
  } | null;
}

/**
 * O OUTRO centro de uma transferência: de onde veio a entrada, para onde foi
 * a saída. O lançamento só conhece a própria perna.
 */
function outraPonta(l: LinhaKardex): { rotulo: string; centro: number; nome: string | null } | null {
  const t = l.transferencia;
  if (!t) return null;
  // Origem = destino é troca de posição feita pela tela de transferência.
  if (t.centroOrigem === t.centroDestino)
    return { rotulo: 'dentro do', centro: t.centroOrigem, nome: t.centroOrigemNome };
  return l.entrada > 0
    ? { rotulo: 'veio do', centro: t.centroOrigem, nome: t.centroOrigemNome }
    : { rotulo: 'foi para o', centro: t.centroDestino, nome: t.centroDestinoNome };
}

interface RelatorioKardex {
  centroEstoque: number;
  centroNome: string | null;
  centroEmpresa: number | null;
  produto: number;
  produtoDescricao: string | null;
  unidade: string | null;
  de: string;
  ate: string;
  saldoAnterior: number;
  totalEntradas: number;
  totalSaidas: number;
  saldoFinal: number;
  linhas: LinhaKardex[];
  /** Entradas/saídas do período por tela de origem — sobre todos os lançamentos. */
  porFormulario: ResumoFormulario[];
  totalLinhas: number;
  truncado: boolean;
  geradoEm: string;
}

interface ResumoFormulario {
  formulario: number | null;
  descricao: string | null;
  modulo: string | null;
  entradas: number;
  saidas: number;
  movimentos: number;
}

/**
 * O texto da origem — o MESMO da coluna "Origem" da grade, porque é ele que
 * o cartão usa para filtrar a grade ao clicar.
 */
const textoOrigem = (formulario: number | null, descricao: string | null): string =>
  descricao ?? (formulario === null ? '(sem origem)' : String(formulario));

/** Kardex de uma faixa de SKUs: um kardex por SKU que mexeu no período. */
interface RelatorioKardexFaixa {
  faixa: true;
  centroEstoque: number;
  centroNome: string | null;
  centroEmpresa: number | null;
  produtoDe: number;
  produtoAte: number;
  de: string;
  ate: string;
  produtos: RelatorioKardex[];
  totalProdutos: number;
  produtosTruncado: boolean;
  linhasTruncado: boolean;
  geradoEm: string;
}

type RespostaKardex = RelatorioKardex | RelatorioKardexFaixa;

const ehFaixa = (r: RespostaKardex): r is RelatorioKardexFaixa => 'faixa' in r && r.faixa;

interface TipoMovimento {
  codigo: number;
  descricao: string;
}

function hojeIso(deslocamentoDias = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + deslocamentoDias);
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** 'AAAA-MM-DD' → '12/09/2026', sem passar por Date (que desloca o fuso). */
function dataBr(iso: string): string {
  const [a, m, d] = iso.slice(0, 10).split('-');
  return d && m && a ? `${d}/${m}/${a}` : iso;
}

function dataHoraBr(texto: string): string {
  const [data = '', hora = ''] = texto.split(' ');
  return `${dataBr(data)} ${hora.slice(0, 8)}`.trim();
}

/**
 * As colunas, num lugar só.
 *
 * Ordenação e filtro trabalham sobre estas definições, então acrescentar
 * coluna é acrescentar uma linha aqui — e ela já nasce clicável e filtrável.
 */
/** A linha da grade: o lançamento mais o SKU a que pertence (para a faixa). */
type LinhaGrade = LinhaKardex & { produto: number; produtoDescricao: string | null };

/** Só na faixa: com um SKU só, ele já está no cabeçalho da folha. */
const COLUNAS_SKU: ColunaGrade<LinhaGrade>[] = [
  {
    chave: 'sku',
    rotulo: 'SKU',
    ordenar: (l) => l.produto,
    texto: (l) => String(l.produto),
  },
  {
    chave: 'descricao',
    rotulo: 'Descrição',
    largura: true,
    ordenar: (l) => l.produtoDescricao ?? '',
    texto: (l) => l.produtoDescricao ?? '(sem descrição)',
  },
];

const COLUNAS: ColunaGrade<LinhaGrade>[] = [
  {
    chave: 'dataHora',
    rotulo: 'Data e hora',
    ordenar: (l) => l.dataHora,
    // Filtro por DIA: filtrar por segundo exato não serve para ninguém.
    texto: (l) => dataBr(l.data),
  },
  {
    chave: 'tipo',
    rotulo: 'Tipo de movimento',
    ordenar: (l) => l.tipoDescricao,
    texto: (l) => l.tipoDescricao,
  },
  {
    chave: 'documento',
    rotulo: 'Documento',
    ordenar: (l) => l.documento ?? -1,
    texto: (l) => (l.documento === null ? '(sem documento)' : String(l.documento)),
  },
  {
    chave: 'origem',
    rotulo: 'Origem (tela do PBS)',
    largura: true,
    ordenar: (l) => l.formularioDescricao ?? '',
    texto: (l) => textoOrigem(l.formulario, l.formularioDescricao),
  },
  {
    chave: 'localizador',
    rotulo: 'Localizador',
    ordenar: (l) => l.localizador ?? -1,
    texto: (l) => (l.localizador === null ? '(sem localizador)' : String(l.localizador)),
  },
  {
    chave: 'endereco',
    rotulo: 'Endereço',
    ordenar: (l) => l.endereco ?? '',
    texto: (l) => l.endereco ?? '(sem endereço)',
  },
  {
    /*
     * A empresa da POSIÇÃO, e não a do centro.
     *
     * `ESTOQUE_LANCAMENTOS` não tem coluna de empresa — só centro e
     * localizador —, e no 2201 (empresa 22) há posições de outra filial com
     * lançamento no livro: medido, 2.994 da 22, 3 da 11 e 2 sem filial. São
     * poucas, mas numa auditoria elas têm de estar VISÍVEIS, e agora dá para
     * filtrar por elas pelo funil da coluna.
     *
     * Não são excluídas: tirar a linha quebraria o acumulado e o saldo
     * deixaria de bater com o próprio livro do PBS.
     */
    chave: 'filial',
    rotulo: 'Empresa',
    ordenar: (l) => l.filial ?? 0,
    texto: (l) => (l.filial === null ? '(sem empresa)' : String(l.filial)),
  },
  {
    chave: 'entrada',
    rotulo: 'Entrada',
    alinhar: 'direita',
    ordenar: (l) => l.entrada,
    texto: (l) => String(l.entrada),
  },
  {
    chave: 'saida',
    rotulo: 'Saída',
    alinhar: 'direita',
    ordenar: (l) => l.saida,
    texto: (l) => String(l.saida),
  },
  {
    chave: 'saldoDepois',
    rotulo: 'Saldo após',
    alinhar: 'direita',
    ordenar: (l) => l.saldoDepois,
    texto: (l) => String(l.saldoDepois),
  },
];

export function Kardex(): JSX.Element {
  /*
   * `centrosDaEmpresa`, e não `centros`: o seletor daqui listava os CDs de
   * TODAS as empresas do usuário, ignorando a empresa escolhida no topo. Quem
   * está operando a 22 via CD da 11 e da 50 na mesma lista — e podia emitir
   * um kardex de outra empresa sem perceber que tinha trocado de contexto.
   */
  const { centro, centrosDaEmpresa, definirCentro } = useContexto();

  /*
   * Duas perguntas de auditoria, na mesma tela.
   *
   * `kardex` responde "o que aconteceu com este SKU" — a razão do estoque, que
   * vem do ERP. `movimentacoes` responde "QUEM mexeu nesta posição", que só o
   * WMS sabe: o ERP registra o efeito no estoque, mas não guarda quem pediu.
   * São a mesma investigação vista de dois ângulos, e por isso moram juntas em
   * vez de virarem mais um item de menu.
   */
  /*
   * O relatório gerado vive na URL (sku, skuAte, de, ate, tipo, aba).
   *
   * Clicar no documento de uma transferência leva para a aprovação; o voltar
   * do navegador tem de trazer o MESMO kardex, senão analisar dez lançamentos
   * vira gerar o relatório dez vezes. Com a URL, o voltar refaz a tela e a
   * resposta sai do cache da consulta, sem ir ao banco de novo.
   */
  const [params] = useSearchParams();
  const atualizarParams = useAtualizarParams();
  const consultaDaUrl = React.useMemo(() => {
    const produto = Number(params.get('sku'));
    if (!Number.isInteger(produto) || produto <= 0) return null;
    const ateUrl = Number(params.get('skuAte'));
    return {
      produto,
      produtoAte: Number.isInteger(ateUrl) && ateUrl >= produto ? ateUrl : produto,
      de: params.get('de') || hojeIso(-30),
      ate: params.get('ate') || hojeIso(),
      tipo: params.get('tipo') ?? '',
    };
  }, [params]);

  const aba: 'kardex' | 'movimentacoes' =
    params.get('aba') === 'movimentacoes' ? 'movimentacoes' : 'kardex';
  const definirAba = (v: 'kardex' | 'movimentacoes'): void =>
    atualizarParams({ aba: v === 'kardex' ? null : v }, { substituir: true });

  const [sku, definirSku] = React.useState(() =>
    consultaDaUrl ? String(consultaDaUrl.produto) : '',
  );
  /** SKU final: vazio (ou igual ao inicial) é o kardex de um SKU só. */
  const [skuAte, definirSkuAte] = React.useState(() =>
    consultaDaUrl && consultaDaUrl.produtoAte > consultaDaUrl.produto
      ? String(consultaDaUrl.produtoAte)
      : '',
  );
  const [de, definirDe] = React.useState(() => consultaDaUrl?.de ?? hojeIso(-30));
  const [ate, definirAte] = React.useState(() => consultaDaUrl?.ate ?? hojeIso());
  const [tipo, definirTipo] = React.useState(() => consultaDaUrl?.tipo ?? '');

  /** Só consulta ao mandar: relatório de auditoria não se refaz a cada tecla. */
  const consulta = consultaDaUrl;
  const definirConsulta = (c: {
    produto: number;
    produtoAte: number;
    de: string;
    ate: string;
    tipo: string;
  }): void =>
    atualizarParams({
      sku: String(c.produto),
      skuAte: c.produtoAte > c.produto ? String(c.produtoAte) : null,
      de: c.de,
      ate: c.ate,
      tipo: c.tipo || null,
    });

  const relatorio = useQuery<RespostaKardex>({
    queryKey: ['kardex', centro, consulta],
    queryFn: () =>
      api.get<RespostaKardex>('/api/relatorios/kardex', {
        centro,
        produto: consulta?.produto,
        ...(consulta && consulta.produtoAte > consulta.produto
          ? { produtoAte: consulta.produtoAte }
          : {}),
        de: consulta?.de,
        ate: consulta?.ate,
        ...(consulta?.tipo ? { tipo: consulta.tipo } : {}),
      }),
    enabled: centro !== null && consulta !== null,
    staleTime: 60_000,
  });

  const tipos = useQuery<{ tipos: TipoMovimento[] }>({
    queryKey: [
      'kardex-tipos',
      centro,
      consulta?.produto,
      consulta?.produtoAte,
      consulta?.de,
      consulta?.ate,
    ],
    queryFn: () =>
      api.get<{ tipos: TipoMovimento[] }>('/api/relatorios/kardex/tipos', {
        centro,
        produto: consulta?.produto,
        produtoAte: consulta?.produtoAte,
        de: consulta?.de,
        ate: consulta?.ate,
      }),
    enabled: centro !== null && consulta !== null,
    staleTime: 60_000,
  });

  const skuInicial = Number(sku.trim());
  const skuFinal = skuAte.trim() === '' ? skuInicial : Number(skuAte.trim());
  const faixaInvertida = sku.trim() !== '' && skuAte.trim() !== '' && skuFinal < skuInicial;

  const enviar = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!Number.isInteger(skuInicial) || skuInicial <= 0 || faixaInvertida) return;
    definirConsulta({ produto: skuInicial, produtoAte: skuFinal, de, ate, tipo });
  };

  const resposta = relatorio.data;
  // O PDF/papel sai pelo Edge, com prévia e escolha de impressora (ver abrirPapelNoEdge).
  const edge = useAbrirNoEdge();

  /** Rótulo dos SKUs para título e nome de arquivo: '30181' ou '30000 a 30500'. */
  const rotuloSkus = resposta
    ? ehFaixa(resposta)
      ? `${resposta.produtoDe} a ${resposta.produtoAte}`
      : String(resposta.produto)
    : '';

  /** Todas as linhas, com o SKU de cada uma — a faixa vira uma planilha só. */
  const linhasParaCsv: (LinhaKardex & { produto: number; produtoDescricao: string | null })[] =
    resposta
      ? (ehFaixa(resposta) ? resposta.produtos : [resposta]).flatMap((p) =>
          p.linhas.map((l) => ({ ...l, produto: p.produto, produtoDescricao: p.produtoDescricao })),
        )
      : [];

  const exportarPdf = (): void => {
    const folha = document.querySelector<HTMLElement>('.folha');
    if (!resposta || !folha) return;
    void edge.abrir({
      modo: 'pagina',
      conteudo: folha,
      titulo: `Kardex ${rotuloSkus} ${resposta.de} a ${resposta.ate}`,
    });
  };

  const exportarCsv = (): void => {
    if (!resposta) return;
    const nome = rotuloSkus.replace(' a ', '-a-');
    baixarCsv(`kardex-${nome}-${resposta.de}-a-${resposta.ate}-${carimboArquivo()}`, linhasParaCsv, [
      { cabecalho: 'SKU', valor: (l) => l.produto, texto: true },
      { cabecalho: 'Produto', valor: (l) => l.produtoDescricao },
      { cabecalho: 'Lançamento', valor: (l) => l.lancamento, texto: true },
      { cabecalho: 'Data', valor: (l) => dataBr(l.data) },
      { cabecalho: 'Data e hora', valor: (l) => dataHoraBr(l.dataHora) },
      { cabecalho: 'Tipo (cód.)', valor: (l) => l.tipoMovimento },
      { cabecalho: 'Tipo de movimento', valor: (l) => l.tipoDescricao },
      { cabecalho: 'Documento', valor: (l) => l.documento, texto: true },
      { cabecalho: 'Origem (tela)', valor: (l) => l.formularioDescricao },
      { cabecalho: 'Módulo', valor: (l) => l.formularioModulo },
      { cabecalho: 'Transferência', valor: (l) => l.transferencia?.transferencia ?? null, texto: true },
      {
        cabecalho: 'Outro centro',
        valor: (l) => {
          const o = outraPonta(l);
          return o ? `${o.centro}${o.nome ? ` ${o.nome}` : ''}` : null;
        },
      },
      { cabecalho: 'Formulário (cód.)', valor: (l) => l.formulario },
      { cabecalho: 'Localizador', valor: (l) => l.localizador, texto: true },
      { cabecalho: 'Endereço', valor: (l) => l.endereco },
      { cabecalho: 'Entrada', valor: (l) => l.entrada },
      { cabecalho: 'Saída', valor: (l) => l.saida },
      { cabecalho: 'Saldo após', valor: (l) => l.saldoDepois },
    ]);
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-2 p-2.5 sm:p-4">
      {/*
        Cabeçalho em UMA linha.

        A versão anterior gastava três: sobrelinha, título grande e uma frase
        explicativa, mais o bloco de abas abaixo e o cartão de filtros abaixo
        dele — quase metade da altura antes do primeiro lançamento aparecer.
        Numa tela de auditoria isso é caro: o valor está na tabela, e cada
        linha de kardex a menos é uma rolagem a mais.

        O título encolheu, a descrição saiu (a tela se explica pelo conteúdo) e
        as abas vieram para a mesma faixa.
      */}
      <header className="nao-imprimir flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2.5">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primaria-suave text-primaria">
            <ScrollText className="h-[18px] w-[18px]" />
          </span>
          <h1 className="text-[17px] font-bold leading-none tracking-[-0.02em]">Kardex</h1>

          <Segmentado
            tamanho="pequeno"
            valor={aba}
            aoMudar={definirAba}
            rotuloAcessivel="O que auditar"
            opcoes={[
              { valor: 'kardex', rotulo: 'Kardex do SKU' },
              { valor: 'movimentacoes', rotulo: 'Auditoria de movimentação' },
            ]}
          />
        </div>
        {resposta ? (
          <div className="flex items-center gap-2">
            <Botao variante="secundaria" tamanho="pequeno" onClick={exportarCsv}>
              <FileDown className="h-4 w-4" />
              Excel / CSV
            </Botao>
            <Botao
              variante="primaria"
              tamanho="pequeno"
              onClick={exportarPdf}
              carregando={edge.abrindo}
              title="Abre o kardex no Edge, com a prévia, a escolha da impressora e o Salvar como PDF."
            >
              <Printer className="h-4 w-4" />
              Exportar PDF
            </Botao>
          </div>
        ) : null}
      </header>
      {edge.erro ? (
        <p className="nao-imprimir flex items-start gap-2 rounded-lg bg-status-divergenciaFundo px-3 py-2 text-[12.5px] text-status-divergencia">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          {edge.erro}
        </p>
      ) : null}

      {aba === 'movimentacoes' ? (
        <LogMovimentacoes centro={centro} />
      ) : (
      <>
      {/* ------------------------------------------------------- filtros */}
      {/*
        Filtros numa FAIXA, não num formulário de cadastro.

        Antes cada campo tinha um rótulo acima, empilhado, e o conjunto virava
        um cartão alto. Aqui o rótulo vira `title` e `aria-label` — continua
        acessível ao leitor de tela e ao mouse parado —, e os campos dividem
        uma linha só. O que se ganha em altura vai inteiro para a tabela.
      */}
      <form
        className="nao-imprimir cartao flex flex-wrap items-center gap-2 px-2.5 py-2"
        onSubmit={enviar}
      >
        <label className="sr-only" htmlFor="kardex-centro">
          Centro de estoque
        </label>
        <select
          id="kardex-centro"
          title="Centro de estoque"
          value={centro ?? ''}
          onChange={(e) => definirCentro(Number(e.target.value))}
          className="min-h-[34px] w-[210px] rounded-lg border border-borda bg-superficie px-2 text-[12.5px] font-semibold shadow-xs"
        >
            {centrosDaEmpresa.map((c) => (
              <option key={c.centroEstoque} value={c.centroEstoque}>
                {c.centroEstoque} · {c.nome}
              </option>
          ))}
        </select>

        <Campo
          value={sku}
          onChange={(e) => definirSku(e.target.value.replace(/\D/g, ''))}
          inputMode="numeric"
          placeholder="SKU inicial"
          title="SKU do produto (ou o inicial da faixa)"
          aria-label="SKU inicial"
          className="tabular h-[34px] w-[104px] text-[13px] font-semibold"
        />
        {/* SKU final opcional: vazio é o kardex de um SKU só, como sempre foi. */}
        <span className="text-[12px] text-textoSuave">até</span>
        <Campo
          value={skuAte}
          onChange={(e) => definirSkuAte(e.target.value.replace(/\D/g, ''))}
          inputMode="numeric"
          placeholder="SKU final"
          title="SKU final da faixa (opcional). Vazio: só o SKU inicial."
          aria-label="SKU final"
          className="tabular h-[34px] w-[104px] text-[13px] font-semibold"
        />

        <span className="flex items-center gap-1">
          <Campo
            type="date"
            value={de}
            onChange={(e) => definirDe(e.target.value)}
            title="Data inicial"
            aria-label="Data inicial"
            className="h-[34px] w-[140px] text-[12.5px]"
          />
          <span className="text-[12px] text-textoSuave">até</span>
          <Campo
            type="date"
            value={ate}
            onChange={(e) => definirAte(e.target.value)}
            title="Data final"
            aria-label="Data final"
            className="h-[34px] w-[140px] text-[12.5px]"
          />
        </span>

        <select
          value={tipo}
          onChange={(e) => definirTipo(e.target.value)}
          title="Tipo de movimento"
          aria-label="Tipo de movimento"
          className="min-h-[34px] w-[168px] rounded-lg border border-borda bg-superficie px-2 text-[12.5px] shadow-xs"
        >
            <option value="">todos</option>
            {(tipos.data?.tipos ?? []).map((t) => (
              <option key={t.codigo} value={t.codigo}>
                {t.descricao}
            </option>
          ))}
        </select>

        <Botao
          type="submit"
          variante="primaria"
          tamanho="pequeno"
          disabled={sku.trim().length === 0 || faixaInvertida}
        >
          <Search className="h-4 w-4" />
          Gerar
        </Botao>

        {faixaInvertida ? (
          <p className="flex items-center gap-1.5 text-[12px] font-semibold text-status-divergencia">
            <AlertTriangle className="h-3.5 w-3.5" />o SKU inicial é maior que o final
          </p>
        ) : null}
        {de > ate ? (
          <p className="flex items-center gap-1.5 text-[12px] font-semibold text-status-divergencia">
            <AlertTriangle className="h-3.5 w-3.5" />a data inicial é maior que a final
          </p>
        ) : null}
      </form>

      {/* ------------------------------------------------------ conteúdo */}
      {consulta === null ? (
        <div className="cartao grid flex-1 place-items-center p-10">
          <EstadoVazio
            icone={<ScrollText className="h-7 w-7" />}
            titulo="Escolha o SKU e o período"
            detalhe="Um SKU, ou uma faixa de SKU inicial a final. Cada SKU sai com o próprio saldo anterior e acumulado."
          />
        </div>
      ) : relatorio.isLoading ? (
        <Esqueleto className="h-64" />
      ) : relatorio.isError ? (
        <EstadoErro erro={relatorio.error} aoTentarDeNovo={() => void relatorio.refetch()} />
      ) : resposta ? (
        ehFaixa(resposta) ? (
          <FolhaKardex produtos={resposta.produtos} faixa={resposta} filtroTipo={consulta.tipo} />
        ) : (
          <FolhaKardex produtos={[resposta]} filtroTipo={consulta.tipo} />
        )
      ) : null}
      </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// A folha — o que vai para o papel
// ---------------------------------------------------------------------------

/**
 * Os dois formulários de transferência entre centros.
 *
 * A mesma transferência aparece no kardex por DOIS registros: a saída vem da
 * aprovação (665962) e a entrada, do recebimento (339905). Por isso o rastro
 * lê o formulário — `REG_MASTER_ORIGEM` significa coisas diferentes em cada um,
 * e o servidor é quem sabe traduzir.
 */
const FORMULARIOS_TRANSFERENCIA = new Set([665962, 339905]);

const ehTransferencia = (formulario: number | null): boolean =>
  formulario !== null && FORMULARIOS_TRANSFERENCIA.has(formulario);

/** "Alocações de Palletes": o REG_MASTER_ORIGEM é a linha de PALETES_ALOCACOES. */
const FORMULARIO_ALOCACAO_PALETE = 456974;

interface DetalheAlocacao {
  paleteAlocacao: number;
  dataHora: string | null;
  usuario: string | null;
  coletor: { cracha: string; operador: string | null; bipadoEm: string | null } | null;
  localizador: number | null;
  endereco: string | null;
  codigoBarras: string | null;
  etiqueta: {
    etiquetaAlocacao: number;
    sequencia: number | null;
    produto: number;
    descricao: string | null;
    quantidade: number;
    ativa: boolean;
  }[];
  cega: {
    recebimento: number;
    criadaEm: string | null;
    fornecedor: string | null;
    aprovadaEm: string | null;
    notas: { numero: number; serie: string | null; nfCompra: number | null }[];
  } | null;
}

/** ISO → '01/10/2026 10:32', na hora local. */
function quandoBr(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * O "de onde veio" de uma entrada por alocação de palete, aberto embaixo da
 * linha do kardex: a alocação (posição, quem bipou), a etiqueta alocada e a
 * cega/NF que a originou. Caixa recuada, com colunas próprias.
 */
function DetalheAlocacaoPalete({ id }: { id: number }): JSX.Element {
  const navegar = useNavigate();
  const q = useQuery<DetalheAlocacao>({
    queryKey: ['kardex-alocacao', id],
    queryFn: () => api.get<DetalheAlocacao>(`/api/relatorios/kardex/alocacao/${id}`),
    staleTime: 5 * 60_000,
  });

  /*
   * Aberto numa linha perto do pé da grade, o detalhe nascia fora da área
   * visível e o clique parecia não ter feito nada. Rola o mínimo para a
   * caixa inteira aparecer — ao abrir e de novo quando os dados chegam,
   * porque ela cresce do esqueleto para o conteúdo.
   */
  const ref = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    const el = ref.current?.closest('tr') ?? ref.current;
    el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [q.isLoading, q.isError]);

  if (q.isLoading) {
    return (
      <div ref={ref}>
        <Esqueleto className="h-16" />
      </div>
    );
  }
  if (q.isError || !q.data) {
    return (
      <div ref={ref}>
        <EstadoErro erro={q.error} aoTentarDeNovo={() => void q.refetch()} />
      </div>
    );
  }
  const d = q.data;
  const bloco = 'min-w-0 space-y-0.5';
  const titulo = 'rotulo mb-0.5 block';
  return (
    <div ref={ref} className="grid gap-x-6 gap-y-2 text-[11.5px] leading-snug sm:grid-cols-3">
      <div className={bloco}>
        <span className={titulo}>Alocação {d.paleteAlocacao}</span>
        <p>
          <span className="text-textoFraco">em</span> <b className="tabular">{quandoBr(d.dataHora)}</b>
        </p>
        <p>
          <span className="text-textoFraco">posição</span>{' '}
          <b className="font-mono">{d.localizador ?? '—'}</b>
          {d.endereco ? <span className="font-mono"> · {d.endereco}</span> : null}
        </p>
        {d.coletor ? (
          <p title={`Gravado no ERP por ${d.usuario ?? '—'}`}>
            <span className="text-textoFraco">bipado por</span>{' '}
            <b>{d.coletor.operador ?? `crachá ${d.coletor.cracha}`}</b>
            <span className="text-textoSuave"> · crachá {d.coletor.cracha}</span>
          </p>
        ) : (
          <p>
            <span className="text-textoFraco">gravado por</span> <b>{d.usuario ?? '—'}</b>
          </p>
        )}
      </div>

      <div className={bloco}>
        <span className={titulo}>Etiqueta alocada</span>
        <p className="font-mono font-semibold">{d.codigoBarras ?? '—'}</p>
        {d.etiqueta.length === 0 ? (
          <p className="text-textoSuave">etiqueta não encontrada</p>
        ) : (
          d.etiqueta.map((e) => (
            <p key={e.etiquetaAlocacao} className="flex min-w-0 items-baseline gap-1.5">
              <span className="tabular font-mono">{e.produto}</span>
              <span className="truncate text-textoFraco">{e.descricao ?? ''}</span>
              <b className="tabular ml-auto shrink-0">{numero(e.quantidade)} un</b>
              {!e.ativa ? (
                <span className="shrink-0 rounded bg-superficie2 px-1 text-[9.5px] font-bold uppercase text-textoFraco">
                  inativa
                </span>
              ) : null}
            </p>
          ))
        )}
      </div>

      <div className={bloco}>
        <span className={titulo}>Cega de origem</span>
        {d.cega ? (
          <>
            <p className="flex flex-wrap items-baseline gap-x-2">
              <b className="tabular">Cega {d.cega.recebimento}</b>
              <button
                type="button"
                onClick={() => navegar(`/recebimento?recebimento=${d.cega!.recebimento}`)}
                className="text-primaria underline decoration-dotted underline-offset-2 hover:decoration-solid"
              >
                Ver cega
              </button>
            </p>
            {d.cega.fornecedor ? <p className="truncate">{d.cega.fornecedor}</p> : null}
            <p className="text-textoFraco">
              criada {quandoBr(d.cega.criadaEm)}
              {d.cega.aprovadaEm ? ` · aprovada ${quandoBr(d.cega.aprovadaEm)}` : ' · não aprovada'}
            </p>
            {d.cega.notas.length > 0 ? (
              <p className="flex flex-wrap gap-x-2">
                {d.cega.notas.map((n) =>
                  n.nfCompra !== null ? (
                    <button
                      key={`${n.numero}-${n.serie}`}
                      type="button"
                      onClick={() => navegar(`/entradas?aba=nfcompra&nf=${n.nfCompra}`)}
                      title="Abrir a NF de compra"
                      className="tabular font-mono text-primaria underline decoration-dotted underline-offset-2 hover:decoration-solid"
                    >
                      NF {n.numero}
                      {n.serie ? `/${n.serie}` : ''}
                    </button>
                  ) : (
                    <span key={`${n.numero}-${n.serie}`} className="tabular font-mono">
                      NF {n.numero}
                      {n.serie ? `/${n.serie}` : ''}
                    </span>
                  ),
                )}
              </p>
            ) : null}
          </>
        ) : (
          <p className="text-textoSuave">sem cega ligada à etiqueta</p>
        )}
      </div>
    </div>
  );
}

/**
 * A folha do kardex — de um SKU ou de uma faixa.
 *
 * Na faixa é UMA grade só, com SKU e descrição nas duas primeiras colunas: o
 * funil e a ordenação valem para todos os SKUs de uma vez, e o PDF e a
 * planilha saem com as mesmas colunas da tela. Na ordem do livro, cada SKU
 * abre com o saldo anterior dele e fecha com o saldo final dele — o
 * acumulado é por SKU, nunca a soma de produtos diferentes.
 */
function FolhaKardex({
  produtos,
  faixa,
  filtroTipo,
}: {
  produtos: RelatorioKardex[];
  /** Presente quando o relatório é de uma faixa de SKUs. */
  faixa?: RelatorioKardexFaixa;
  filtroTipo: string;
}): JSX.Element {
  const navegar = useNavigate();
  const ehDeFaixa = faixa !== undefined;
  // Cabeçalho comum: o primeiro SKU traz centro, período e emissão (iguais em todos).
  const base = produtos[0];
  const centroEstoque = faixa?.centroEstoque ?? base?.centroEstoque ?? 0;
  const centroNome = faixa?.centroNome ?? base?.centroNome ?? null;
  const centroEmpresa = faixa?.centroEmpresa ?? base?.centroEmpresa ?? null;
  const de = faixa?.de ?? base?.de ?? '';
  const ate = faixa?.ate ?? base?.ate ?? '';
  const geradoEm = faixa?.geradoEm ?? base?.geradoEm ?? new Date().toISOString();

  /*
   * Resolve o par (formulário, registro) no número da transferência e navega.
   * A tradução fica no SERVIDOR porque a regra de qual formulário significa o
   * quê é do ERP, não da interface — repeti-la aqui seria uma segunda verdade.
   */
  const abrirTransferencia = async (
    formulario: number,
    reg: number,
    conhecida?: number,
  ): Promise<void> => {
    if (conhecida) {
      navegar(`/transferencias?aba=centros&transferencia=${conhecida}`);
      return;
    }
    try {
      const r = await api.get<{ transferencia: number | null }>(
        `/api/transferencias/rastro/${formulario}/${reg}`,
      );
      if (r.transferencia !== null) {
        navegar(`/transferencias?aba=centros&transferencia=${r.transferencia}`);
      }
    } catch {
      /* Sem rastro não há para onde ir; a linha do kardex continua legível. */
    }
  };

  const colunas = React.useMemo(
    () => (ehDeFaixa ? [...COLUNAS_SKU, ...COLUNAS] : COLUNAS),
    [ehDeFaixa],
  );
  const totalColunas = colunas.length;

  const linhas = React.useMemo<LinhaGrade[]>(
    () =>
      produtos.flatMap((p) =>
        p.linhas.map((l) => ({ ...l, produto: p.produto, produtoDescricao: p.produtoDescricao })),
      ),
    [produtos],
  );

  // Chave do relatório: filtro de outro SKU/período não faz sentido aqui.
  const chaveRelatorio = `${centroEstoque}|${de}|${ate}|${produtos.map((p) => p.produto).join(',')}`;
  const grade = useGrade<LinhaGrade>(
    linhas,
    colunas,
    (a, b) => a.produto - b.produto || a.lancamento - b.lancamento,
    `kardex-grade:${chaveRelatorio}`,
  );
  const visiveis = grade.visiveis;
  const naOrdemDoLivro = grade.ordem === null;
  const temFiltro = grade.temFiltro;
  const comSeparadores = naOrdemDoLivro && !temFiltro;

  /*
   * A rolagem também volta: ao retornar da transferência, a grade abre na
   * linha que estava sendo conferida, e não no topo do livro.
   */
  const refRolagem = React.useRef<HTMLDivElement | null>(null);
  const chaveRolagem = `kardex-rolagem:${chaveRelatorio}`;
  const guardarRolagem = (e: React.UIEvent<HTMLDivElement>): void => {
    try {
      sessionStorage.setItem(chaveRolagem, String(e.currentTarget.scrollTop));
    } catch {
      /* sem storage, só não lembra */
    }
  };
  React.useLayoutEffect(() => {
    try {
      const y = Number(sessionStorage.getItem(chaveRolagem));
      if (refRolagem.current && y > 0) refRolagem.current.scrollTop = y;
    } catch {
      /* idem */
    }
    // Só na montagem e na troca de relatório.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveRolagem]);

  // Some tudo ao trocar de relatório — mas não ao VOLTAR para ele: na
  // montagem a grade acabou de ler o filtro lembrado deste mesmo relatório.
  const primeiraChave = React.useRef(chaveRelatorio);
  React.useEffect(() => {
    if (primeiraChave.current === chaveRelatorio) return;
    primeiraChave.current = '';
    grade.limpar();
    // `grade` muda a cada render; depender dela reexecutaria o efeito sempre.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveRelatorio]);

  const somaVisivel = React.useMemo(
    () => ({
      entradas: visiveis.reduce((a, l) => a + l.entrada, 0),
      saidas: visiveis.reduce((a, l) => a + l.saida, 0),
    }),
    [visiveis],
  );

  const totais = React.useMemo(
    () => ({
      entradas: produtos.reduce((a, p) => a + p.totalEntradas, 0),
      saidas: produtos.reduce((a, p) => a + p.totalSaidas, 0),
      movimentos: produtos.reduce((a, p) => a + p.totalLinhas, 0),
      exibidas: produtos.reduce((a, p) => a + p.linhas.length, 0),
    }),
    [produtos],
  );
  const algumTruncado = produtos.some((p) => p.truncado);

  /*
   * Entradas e saídas por tela de origem.
   *
   * Sem filtro nas outras colunas, vem do servidor: é o período inteiro,
   * mesmo quando as linhas foram cortadas. Com filtro, é o que passa por ele
   * — o cartão acompanha a grade, como os totais do rodapé.
   */
  const porOrigem = React.useMemo(() => {
    const mapa = new Map<string, { chave: string; modulo: string | null; entradas: number; saidas: number }>();
    const somar = (chave: string, modulo: string | null, entradas: number, saidas: number): void => {
      const atual = mapa.get(chave) ?? { chave, modulo, entradas: 0, saidas: 0 };
      atual.entradas += entradas;
      atual.saidas += saidas;
      mapa.set(chave, atual);
    };
    // O filtro da própria origem não conta: com uma origem marcada, as outras
    // continuam no cartão para trocar de uma para outra (como no Excel).
    const outrosFiltros = colunas
      .filter((c) => c.chave !== 'origem')
      .map((c) => ({ c, f: grade.filtroDe(c.chave) }))
      .filter((x) => x.f !== undefined && x.f.size > 0);
    if (outrosFiltros.length > 0) {
      for (const l of linhas) {
        if (!outrosFiltros.every(({ c, f }) => f!.has(c.texto(l)))) continue;
        somar(textoOrigem(l.formulario, l.formularioDescricao), l.formularioModulo, l.entrada, l.saida);
      }
    } else {
      for (const p of produtos) {
        for (const f of p.porFormulario ?? []) {
          somar(textoOrigem(f.formulario, f.descricao), f.modulo, f.entradas, f.saidas);
        }
      }
    }
    const lista = [...mapa.values()];
    return {
      entradas: lista
        .filter((o) => o.entradas > 0)
        .sort((a, b) => b.entradas - a.entradas)
        .map((o) => ({ chave: o.chave, modulo: o.modulo, valor: o.entradas })),
      saidas: lista
        .filter((o) => o.saidas > 0)
        .sort((a, b) => b.saidas - a.saidas)
        .map((o) => ({ chave: o.chave, modulo: o.modulo, valor: o.saidas })),
    };
    // `grade` muda a cada render; os filtros entram pela lista visível.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visiveis, linhas, produtos, colunas]);

  /** A origem filtrada na grade (uma só), para marcar a linha do cartão. */
  const filtroOrigem = grade.filtroDe('origem');
  const origemAtiva = filtroOrigem && filtroOrigem.size === 1 ? ([...filtroOrigem][0] ?? null) : null;
  /** Clicar filtra a grade por aquela origem; clicar de novo desfaz. */
  const alternarOrigem = (chave: string): void =>
    grade.definirFiltro('origem', origemAtiva === chave ? new Set() : new Set([chave]));
  const porSku = React.useMemo(() => new Map(produtos.map((p) => [p.produto, p])), [produtos]);

  const emissao = new Date(geradoEm);
  const p2 = (n: number): string => String(n).padStart(2, '0');
  const carimbo = `${p2(emissao.getDate())}/${p2(emissao.getMonth() + 1)}/${emissao.getFullYear()} ${p2(
    emissao.getHours(),
  )}:${p2(emissao.getMinutes())}`;

  /** Lançamentos com o detalhe aberto embaixo (alocação de palete). */
  const [abertas, definirAbertas] = React.useState<Set<number>>(() => new Set());
  const alternarAberta = (lancamento: number): void =>
    definirAbertas((atual) => {
      const nova = new Set(atual);
      if (nova.has(lancamento)) nova.delete(lancamento);
      else nova.add(lancamento);
      return nova;
    });
  const ehAlocacao = (l: LinhaGrade): boolean =>
    l.formulario === FORMULARIO_ALOCACAO_PALETE && l.regMasterOrigem !== null;

  /** Uma linha de lançamento da grade e, se aberta, o detalhe embaixo. */
  const linhaLancamento = (l: LinhaGrade): JSX.Element => (
    <React.Fragment key={l.lancamento}>
      {linhaPrincipal(l)}
      {ehAlocacao(l) && abertas.has(l.lancamento) ? (
        /* scroll-mb: o rodapé de totais é sticky no pé e cobriria a caixa. */
        <tr className="scroll-mb-12 scroll-mt-10 border-b border-borda/60">
          <td colSpan={totalColunas} className="px-2.5 pb-2 pt-1">
            <div className="ml-6 rounded-lg border border-borda border-l-2 border-l-primaria bg-superficie2/50 px-3 py-2">
              <DetalheAlocacaoPalete id={l.regMasterOrigem!} />
            </div>
          </td>
        </tr>
      ) : null}
    </React.Fragment>
  );

  const linhaPrincipal = (l: LinhaGrade): JSX.Element => (
    <tr
      className={cn(
        'border-b border-borda/60 hover:bg-superficie2',
        abertas.has(l.lancamento) && 'bg-primaria-suave/40',
      )}
    >
      {ehDeFaixa ? (
        <>
          <Td>
            <span className="tabular font-mono font-semibold">{l.produto}</span>
          </Td>
          <Td largura>
            <span className="leading-tight">{l.produtoDescricao ?? '—'}</span>
          </Td>
        </>
      ) : null}
      <Td>
        <span className="tabular font-mono">{dataHoraBr(l.dataHora)}</span>
      </Td>
      <Td>
        <span className="inline-flex items-center gap-1.5">
          {l.entrada > 0 ? (
            <ArrowDownToLine className="h-3.5 w-3.5 text-status-livre" />
          ) : (
            <ArrowUpFromLine className="h-3.5 w-3.5 text-status-divergencia" />
          )}
          {l.tipoDescricao}
          {l.tipoMovimento !== null ? (
            <span className="text-textoSuave">({l.tipoMovimento})</span>
          ) : null}
        </span>
      </Td>
      <Td>
        {/*
          O DOCUMENTO VIRA LINK quando dá para voltar até a origem.
          Uma saída por transferência responde "quanto saiu"; a
          pergunta seguinte é sempre "quem autorizou", e até aqui ela
          só tinha resposta no Procfit.
        */}
        {ehTransferencia(l.formulario) && l.regMasterOrigem !== null ? (
          <button
            type="button"
            onClick={() =>
              abrirTransferencia(l.formulario!, l.regMasterOrigem!, l.transferencia?.transferencia)
            }
            title="Abrir a transferência que gerou este lançamento"
            className="tabular font-mono text-primaria underline decoration-dotted underline-offset-2 hover:decoration-solid"
          >
            {l.documento ?? '—'}
          </button>
        ) : ehAlocacao(l) ? (
          /* Alocação de palete: abre embaixo a etiqueta, a posição e a cega. */
          <button
            type="button"
            onClick={() => alternarAberta(l.lancamento)}
            aria-expanded={abertas.has(l.lancamento)}
            title={
              abertas.has(l.lancamento)
                ? 'Fechar o detalhe da alocação'
                : 'Ver a etiqueta alocada, a posição e a cega de origem'
            }
            className="inline-flex items-center gap-1 text-primaria underline decoration-dotted underline-offset-2 hover:decoration-solid"
          >
            <ChevronRight
              className={cn(
                'h-3.5 w-3.5 transition-transform',
                abertas.has(l.lancamento) && 'rotate-90',
              )}
            />
            <span className="tabular font-mono">{l.documento ?? l.regMasterOrigem}</span>
          </button>
        ) : (
          <span className="tabular font-mono">{l.documento ?? '—'}</span>
        )}
      </Td>
      <Td largura>
        {l.formularioDescricao ? (
          <span className="leading-tight">
            {l.formularioDescricao}
            {l.formularioModulo ? (
              <span className="ml-1 rounded bg-superficie2 px-1 py-px text-[9.5px] font-bold uppercase text-textoFraco">
                {l.formularioModulo}
              </span>
            ) : null}
          </span>
        ) : (
          <span className="tabular font-mono text-textoSuave">{l.formulario ?? '—'}</span>
        )}
        {(() => {
          const o = outraPonta(l);
          return o ? (
            <span
              className="mt-0.5 block text-[11px] text-textoFraco"
              title={`Transferência ${l.transferencia!.transferencia}: ${l.transferencia!.centroOrigem} → ${l.transferencia!.centroDestino}`}
            >
              {o.rotulo}{' '}
              <strong className="tabular font-mono text-texto">{o.centro}</strong>
              {o.nome ? ` · ${o.nome}` : ''}
            </span>
          ) : null;
        })()}
      </Td>
      <Td>
        <span className="tabular font-mono">{l.localizador ?? '—'}</span>
      </Td>
      <Td>
        <span className="font-mono">{l.endereco ?? '—'}</span>
      </Td>
      {/* Empresa da posição: discreta quando é a do centro, em destaque
          quando NÃO é — numa auditoria, linha de outra empresa no meio
          do livro precisa saltar aos olhos. */}
      <Td>
        {l.filial === null ? (
          <span className="text-textoSuave">—</span>
        ) : l.filial === centroEmpresa ? (
          <span className="tabular font-mono text-textoFraco">{l.filial}</span>
        ) : (
          <span
            title={`Posição da empresa ${l.filial}, e este centro é da ${centroEmpresa}. A linha fica no relatório porque o saldo acumulado depende dela.`}
            className="tabular rounded bg-status-atencaoFundo px-1.5 py-0.5 font-mono text-[11px] font-bold text-status-atencao"
          >
            {l.filial}
          </span>
        )}
      </Td>
      <Td alinhar="direita">
        {l.entrada > 0 ? (
          <b className="text-status-livre">{numero(l.entrada)}</b>
        ) : (
          <span className="text-textoSuave">—</span>
        )}
      </Td>
      <Td alinhar="direita">
        {l.saida > 0 ? (
          <b className="text-status-divergencia">{numero(l.saida)}</b>
        ) : (
          <span className="text-textoSuave">—</span>
        )}
      </Td>
      <Td alinhar="direita">
        <b className={cn('tabular', l.saldoDepois < 0 && 'text-status-divergencia')}>
          {numero(l.saldoDepois)}
        </b>
      </Td>
    </tr>
  );

  /** Abertura (saldo anterior) e, na faixa, fechamento de cada SKU. */
  const linhaSaldo = (
    chave: string,
    rotulo: React.ReactNode,
    valor: number,
    fechamento = false,
  ): JSX.Element => (
    <tr
      key={chave}
      className={cn(
        'border-b border-borda font-semibold',
        fechamento ? 'bg-superficie2/30 text-textoFraco' : 'bg-superficie2/60',
      )}
    >
      <Td colSpan={totalColunas - 1}>{rotulo}</Td>
      <Td alinhar="direita">
        <span className={cn('tabular', valor < 0 && 'text-status-divergencia')}>
          {numero(valor)}
        </span>
      </Td>
    </tr>
  );

  /** O corpo na ordem do livro: SKU a SKU, cada um entre seus dois saldos. */
  const corpoNoLivro = (): JSX.Element[] => {
    const saida: JSX.Element[] = [];
    let atual: number | null = null;
    const fechar = (sku: number): void => {
      const p = porSku.get(sku);
      if (ehDeFaixa && p) {
        saida.push(
          linhaSaldo(`fim-${sku}`, `Saldo final do SKU ${sku} em ${dataBr(ate)}`, p.saldoFinal, true),
        );
      }
    };
    for (const l of visiveis) {
      if (l.produto !== atual) {
        if (atual !== null) fechar(atual);
        atual = l.produto;
        const p = porSku.get(l.produto);
        saida.push(
          linhaSaldo(
            `ini-${l.produto}`,
            ehDeFaixa ? (
              <>
                SKU <span className="font-mono">{l.produto}</span>
                {l.produtoDescricao ? ` · ${l.produtoDescricao}` : ''} · saldo anterior a{' '}
                {dataBr(de)}
              </>
            ) : (
              `Saldo anterior a ${dataBr(de)}`
            ),
            p?.saldoAnterior ?? 0,
          ),
        );
      }
      saida.push(linhaLancamento(l));
    }
    if (atual !== null) fechar(atual);
    return saida;
  };

  return (
    <div className="folha cartao flex min-h-0 flex-1 flex-col overflow-hidden p-0">
      {/* Identificação do documento. Repete no topo de cada página impressa. */}
      <div className="cabecalho-folha border-b border-borda px-4 py-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-[15px] font-bold tracking-[-0.01em]">
              Kardex · movimentação no período
            </p>
            {faixa ? (
              <p className="text-[12.5px] text-textoFraco">
                SKUs <b className="font-mono text-texto">{faixa.produtoDe}</b> a{' '}
                <b className="font-mono text-texto">{faixa.produtoAte}</b> ·{' '}
                {numero(produtos.length)} {produtos.length === 1 ? 'SKU' : 'SKUs'} com movimento
              </p>
            ) : base ? (
              <p className="text-[12.5px] text-textoFraco">
                SKU <b className="font-mono text-texto">{base.produto}</b>
                {base.produtoDescricao ? ` · ${base.produtoDescricao}` : ''}
                {base.unidade ? ` · ${base.unidade}` : ''}
              </p>
            ) : null}
            <p className="text-[12px] text-textoFraco">
              Centro <b className="text-texto">{centroEstoque}</b>
              {centroNome ? ` · ${centroNome}` : ''} · período{' '}
              <b className="text-texto">{dataBr(de)}</b> a <b className="text-texto">{dataBr(ate)}</b>
              {filtroTipo ? ' · filtrado por tipo de movimento' : ''}
            </p>
          </div>
          <p className="text-right text-[11px] text-textoFraco">
            emitido em {carimbo}
            <br />
            fonte: ESTOQUE_LANCAMENTOS (PBS)
          </p>
        </div>

        {/*
          Saldos à esquerda; entradas e saídas abertas por tela de origem.
          Cada origem é um filtro: clicar mostra na grade só os lançamentos
          dela, clicar de novo volta tudo.
        */}
        <div className="mt-2.5 grid gap-2 md:grid-cols-[auto_minmax(0,1fr)_minmax(0,1fr)]">
          <div className="flex flex-wrap content-start gap-2 md:flex-col md:flex-nowrap">
            {/* Na faixa não há "saldo" de cartão: somar saldo de produtos
                diferentes dá um número que não existe. Cada SKU traz o seu
                na grade. */}
            {!faixa && base ? <Cartao rotulo="Saldo anterior" valor={base.saldoAnterior} /> : null}
            {!faixa && base ? <Cartao rotulo="Saldo final" valor={base.saldoFinal} destaque /> : null}
            <span className="flex items-center">
              <Pilula tom="neutro">
                {numero(totais.movimentos)} {totais.movimentos === 1 ? 'movimento' : 'movimentos'}
              </Pilula>
            </span>
          </div>
          <CartaoPorOrigem
            titulo="Entradas"
            tom="entrada"
            itens={porOrigem.entradas}
            ativa={origemAtiva}
            aoClicar={alternarOrigem}
          />
          <CartaoPorOrigem
            titulo="Saídas"
            tom="saida"
            itens={porOrigem.saidas}
            ativa={origemAtiva}
            aoClicar={alternarOrigem}
          />
        </div>

        {faixa?.produtosTruncado ? (
          <p className="nao-imprimir mt-2 flex items-start gap-2 rounded-lg border border-status-atencao/30 bg-status-atencaoFundo px-3 py-1.5 text-[12px] text-textoFraco">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-status-atencao" />
            A faixa tem {numero(faixa.totalProdutos)} SKUs com movimento; mostrando os{' '}
            {numero(produtos.length)} primeiros. Para ver o resto, estreite a faixa.
          </p>
        ) : null}
        {algumTruncado ? (
          <p className="nao-imprimir mt-2 flex items-start gap-2 rounded-lg border border-status-atencao/30 bg-status-atencaoFundo px-3 py-1.5 text-[12px] text-textoFraco">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-status-atencao" />
            Mostrando {numero(totais.exibidas)} das {numero(totais.movimentos)} linhas do período.
            Os totais e saldos consideram todas; para auditar o resto, estreite o período
            {faixa ? ' ou a faixa' : ''}.
          </p>
        ) : null}
      </div>

      {/* Aviso honesto: fora da ordem do livro, "saldo após" continua sendo o
          saldo NAQUELE lançamento — não a soma da coluna de cima para baixo. */}
      {!comSeparadores && linhas.length > 0 ? (
        <p className="nao-imprimir flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-borda bg-primaria-suave px-4 py-1.5 text-[11.5px]">
          <Filter className="h-3.5 w-3.5 shrink-0 text-primaria" />
          <span>
            Mostrando <b>{numero(visiveis.length)}</b> de {numero(linhas.length)} linhas
            {!naOrdemDoLivro ? ' · ordenado por coluna' : ''} · entradas{' '}
            <b className="text-status-livre">{numero(somaVisivel.entradas)}</b> · saídas{' '}
            <b className="text-status-divergencia">{numero(somaVisivel.saidas)}</b>
          </span>
          {!naOrdemDoLivro ? (
            <span className="text-textoFraco">
              — fora da ordem cronológica, <b>saldo após</b> é o saldo daquele lançamento, não a
              soma da coluna.
            </span>
          ) : null}
          <Botao variante="fantasma" tamanho="micro" className="ml-auto" onClick={grade.limpar}>
            <RotateCcw className="h-3.5 w-3.5" />
            Voltar à ordem do livro
          </Botao>
        </p>
      ) : null}

      <div ref={refRolagem} onScroll={guardarRolagem} className="min-h-0 flex-1 overflow-auto">
        <table className="tabela-kardex w-full border-collapse text-[12px]">
          <thead>
            <tr className="text-left">
              {colunas.map((c) => (
                <CabecalhoColuna key={c.chave} coluna={c} grade={grade} />
              ))}
            </tr>
          </thead>
          <tbody>
            {/* As linhas de saldo só fazem sentido na ordem do livro: em lista
                ordenada ou filtrada elas pareceriam pertencer à linha vizinha. */}
            {comSeparadores
              ? corpoNoLivro()
              : visiveis.map((l) => linhaLancamento(l))}

            {/* SKU único sem movimento: ainda mostra o saldo anterior. */}
            {comSeparadores && !ehDeFaixa && visiveis.length === 0 && base
              ? linhaSaldo('ini', `Saldo anterior a ${dataBr(de)}`, base.saldoAnterior)
              : null}

            {visiveis.length === 0 ? (
              <tr>
                <Td colSpan={totalColunas}>
                  <p className="py-8 text-center text-textoFraco">
                    {linhas.length === 0
                      ? ehDeFaixa
                        ? 'Nenhum SKU da faixa teve movimento no período.'
                        : 'Nenhum movimento deste SKU no período.'
                      : 'Nenhuma linha passa pelos filtros das colunas.'}
                  </p>
                </Td>
              </tr>
            ) : null}
          </tbody>
          <tfoot>
            <tr className="font-bold [&>td]:sticky [&>td]:bottom-0 [&>td]:border-t-2 [&>td]:border-bordaForte [&>td]:bg-superficie2">
              <Td colSpan={totalColunas - 3}>
                {temFiltro ? 'Totais do que está visível' : 'Totais do período'}
              </Td>
              <Td alinhar="direita">{numero(temFiltro ? somaVisivel.entradas : totais.entradas)}</Td>
              <Td alinhar="direita">{numero(temFiltro ? somaVisivel.saidas : totais.saidas)}</Td>
              <Td alinhar="direita">
                {temFiltro || ehDeFaixa || !base ? '—' : numero(base.saldoFinal)}
              </Td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/**
 * Entradas (ou saídas) do período abertas por tela de origem.
 *
 * Cada linha é um botão de filtro: "30 entraram por Cancelamento de NF-e",
 * clica e a grade mostra só esses 30; clica de novo e volta tudo. A barra é
 * a fatia daquela origem no total do cartão.
 */
function CartaoPorOrigem({
  titulo,
  tom,
  itens,
  ativa,
  aoClicar,
}: {
  titulo: string;
  tom: 'entrada' | 'saida';
  itens: { chave: string; modulo: string | null; valor: number }[];
  ativa: string | null;
  aoClicar: (chave: string) => void;
}): JSX.Element {
  const total = itens.reduce((a, i) => a + i.valor, 0);
  const corTexto = tom === 'entrada' ? 'text-status-livre' : 'text-status-divergencia';
  const corBarra = tom === 'entrada' ? 'bg-status-livre' : 'bg-status-divergencia';
  const verbo = tom === 'entrada' ? 'entraram' : 'saíram';
  return (
    <div className="flex min-w-0 flex-col rounded-lg border border-borda bg-superficie px-2.5 py-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className="rotulo">{titulo} por origem</span>
        <b className={cn('tabular text-[15px] leading-tight', corTexto)}>{numero(total)}</b>
      </div>
      {itens.length === 0 ? (
        <p className="py-1 text-[11.5px] text-textoSuave">
          nenhuma {tom === 'entrada' ? 'entrada' : 'saída'} no período
        </p>
      ) : (
        <ul className="-mx-1 mt-1 max-h-[112px] space-y-px overflow-auto">
          {itens.map((i) => {
            const pct = total > 0 ? (i.valor / total) * 100 : 0;
            const marcada = ativa === i.chave;
            const apagada = ativa !== null && !marcada;
            return (
              <li key={i.chave}>
                <button
                  type="button"
                  onClick={() => aoClicar(i.chave)}
                  aria-pressed={marcada}
                  title={
                    marcada
                      ? 'Clique para mostrar todos os lançamentos de novo'
                      : `${numero(i.valor)} ${verbo} por ${i.chave}. Clique para ver só esses lançamentos.`
                  }
                  className={cn(
                    'group grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 rounded-md px-1 py-0.5 text-left transition-colors',
                    marcada
                      ? 'bg-primaria-suave ring-1 ring-primaria/40'
                      : 'hover:bg-superficie2',
                    apagada && 'opacity-55 hover:opacity-100',
                  )}
                >
                  <span className="flex min-w-0 items-center gap-1 text-[11.5px] leading-tight">
                    <span className="truncate">{i.chave}</span>
                    {i.modulo ? (
                      <span className="shrink-0 rounded bg-superficie2 px-1 py-px text-[9px] font-bold uppercase text-textoFraco">
                        {i.modulo}
                      </span>
                    ) : null}
                  </span>
                  <span className="flex items-center gap-1.5 text-[11.5px]">
                    <b className="tabular">{numero(i.valor)}</b>
                    <span className="tabular w-8 text-right text-[10.5px] text-textoSuave">
                      {pct >= 1 ? Math.round(pct) : '<1'}%
                    </span>
                    {marcada ? (
                      <X className="h-3 w-3 text-primaria" />
                    ) : (
                      <Filter className="h-3 w-3 text-textoSuave opacity-0 transition-opacity group-hover:opacity-100" />
                    )}
                  </span>
                  <span className="col-span-2 mt-0.5 h-[3px] overflow-hidden rounded-full bg-superficie2">
                    <span
                      className={cn('block h-full rounded-full opacity-70', corBarra)}
                      style={{ width: `${Math.max(pct, 1)}%` }}
                    />
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function Cartao({
  rotulo,
  valor,
  tom,
  destaque = false,
}: {
  rotulo: string;
  valor: number;
  tom?: 'entrada' | 'saida';
  destaque?: boolean;
}): JSX.Element {
  return (
    <span
      className={cn(
        'rounded-lg border px-2.5 py-1.5 leading-tight',
        destaque ? 'border-primaria/40 bg-primaria-suave' : 'border-borda bg-superficie',
      )}
    >
      <span className="rotulo block">{rotulo}</span>
      <b
        className={cn(
          'tabular text-[15px]',
          tom === 'entrada' && 'text-status-livre',
          tom === 'saida' && 'text-status-divergencia',
          destaque && 'text-primaria',
          valor < 0 && 'text-status-divergencia',
        )}
      >
        {numero(valor)}
      </b>
    </span>
  );
}

function Td({
  children,
  alinhar,
  colSpan,
  largura = false,
}: {
  children: React.ReactNode;
  alinhar?: 'direita';
  colSpan?: number;
  /** Deixa a célula quebrar linha — para textos longos como o nome da tela. */
  largura?: boolean;
}): JSX.Element {
  return (
    <td
      colSpan={colSpan}
      className={cn(
        'px-2.5 py-1',
        largura ? 'min-w-[180px] max-w-[280px]' : 'whitespace-nowrap',
        alinhar === 'direita' && 'text-right',
      )}
    >
      {children}
    </td>
  );
}
